import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Table, message, Space, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { Report } from "../../api";
import { NotifyGroupAPI } from "../../constants/GenericAPI";

export const PickupSubIdentifiersModal = ({ visible, onCancel, onPickup, selectedGroup, selectedSubIdentifiers }) => {
  const [subIdentifiersOptions, setSubIdentifiersOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]); // 改為陣列以支持多選
  // 添加一個 ref 來追蹤組件是否已卸載
  const isMounted = useRef(true);
  // 添加一個 ref 來追蹤 Modal 是否可見
  const isVisibleRef = useRef(visible);

  // 在組件卸載時設置 isMounted 為 false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 更新 isVisibleRef 並處理 modal 關閉時的清理工作
  useEffect(() => {
    isVisibleRef.current = visible;
    if (!visible) {
      setSearchText("");
      setFilteredOptions(subIdentifiersOptions);
    }
  }, [visible, subIdentifiersOptions]);

  // 獲取子識別碼選項
  const fetchSubIdentifiersOptions = async () => {
    if (!isVisibleRef.current) return;

    setLoading(true);
    try {
      const optionResponse = await Report.GetImageReportOption();
      if (isMounted.current && isVisibleRef.current) {
        if (optionResponse.code === 200) {
          const data = optionResponse.data || [];
          setSubIdentifiersOptions(data);
          setFilteredOptions(data);

          // 如果有已選擇的設定，設置選中狀態和名稱
          if (selectedSubIdentifiers && Array.isArray(selectedSubIdentifiers)) {
            setSelectedRowKeys(selectedSubIdentifiers);
            const selectedOptions = data.filter(item => selectedSubIdentifiers.includes(item.id));
            setSelectedNames(selectedOptions.map(item => item.name));
          } else {
            setSelectedRowKeys([]);
            setSelectedNames([]);
          }
        }
      }
    } catch (error) {
      console.error("獲取子識別碼選項失敗:", error);
      if (isMounted.current && isVisibleRef.current) {
        message.error("獲取子識別碼選項失敗");
      }
    } finally {
      if (isMounted.current && isVisibleRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (visible) {
      fetchSubIdentifiersOptions();
      if (selectedSubIdentifiers && Array.isArray(selectedSubIdentifiers)) {
        setSelectedRowKeys(selectedSubIdentifiers);
        // 名稱會在fetch後設置
      } else {
        setSelectedRowKeys([]);
        setSelectedNames([]);
      }
      // 重置搜尋
      setSearchText("");
    }
  }, [visible, selectedSubIdentifiers]);

  // 處理搜尋
  useEffect(() => {
    if (searchText === "") {
      setFilteredOptions(subIdentifiersOptions);
      return;
    }
    const searchLower = searchText.toLowerCase();
    const filtered = subIdentifiersOptions.filter((item) => item.name?.toLowerCase().includes(searchLower));
    setFilteredOptions(filtered);
  }, [searchText, subIdentifiersOptions]);

  // 處理選擇行的變更
  const handleRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
    // 設置選中的名稱
    const selectedOptions = subIdentifiersOptions.filter(item => selectedKeys.includes(item.id));
    setSelectedNames(selectedOptions.map(item => item.name));
  };

  // 處理點擊行的事件
  const handleRowClick = (record) => {
    const newSelectedKeys = selectedRowKeys.includes(record.id)
      ? selectedRowKeys.filter(key => key !== record.id)
      : [...selectedRowKeys, record.id];
    setSelectedRowKeys(newSelectedKeys);
    const selectedOptions = subIdentifiersOptions.filter(item => newSelectedKeys.includes(item.id));
    setSelectedNames(selectedOptions.map(item => item.name));
  };

  // 處理選擇設定的確認
  const handlePickSetting = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("請先選擇至少一個子識別碼");
      return;
    }

    try {
      const subIds = selectedRowKeys;
      // 更新 selectedGroup 的 sub_identifiers
      const updatedGroup = {
        ...selectedGroup,
        sub_identifiers: JSON.stringify(subIds),
      };

      // 更新群組設定
      await NotifyGroupAPI.update(selectedGroup.id, updatedGroup);

      onPickup(subIds);

      // 關閉 Modal
      onCancel();
    } catch (error) {
      console.error("選擇子識別碼失敗:", error);
      if (isMounted.current && isVisibleRef.current) {
        message.error("選擇子識別碼失敗");
      }
    }
  };

  // 處理刪除已選擇的子識別碼
  const handleRemoveSubIdentifiers = async () => {
    try {
      // 更新 selectedGroup 的 sub_identifiers，設為 null 或空
      const updatedGroup = {
        ...selectedGroup,
        sub_identifiers: JSON.stringify([]),
      };

      // 更新群組設定
      await NotifyGroupAPI.update(selectedGroup.id, updatedGroup);

      // 通知父組件已刪除設定
      onPickup(null);

      // 顯示成功訊息
      message.success("已移除子識別碼設定");

      // 關閉 Modal
      onCancel();
    } catch (error) {
      console.error("移除子識別碼設定失敗:", error);
      if (isMounted.current && isVisibleRef.current) {
        message.error("移除子識別碼設定失敗");
      }
    }
  };

  // 定義表格列
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      align: "center",
      key: "id",
      width: 60,
    },
    {
      title: "名稱",
      dataIndex: "name",
      key: "name",
    },
  ];

  // 確定 Modal 的 footer 按鈕
  const modalFooter = [
    <Button key="cancel" onClick={onCancel}>
      取消
    </Button>,
  ];

  // 如果已經有選擇的子識別碼，添加刪除按鈕
  if (selectedSubIdentifiers && selectedSubIdentifiers.length > 0) {
    modalFooter.unshift(
      <Button key="remove" danger onClick={handleRemoveSubIdentifiers} icon={<DeleteOutlined />}>
        移除設定
      </Button>
    );
  }

  // 添加確認選擇按鈕
  modalFooter.push(
    <Button key="pick" type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} onClick={handlePickSetting} disabled={selectedRowKeys.length === 0}>
      確認選擇
    </Button>
  );

  return (
    <Modal title={`選擇子識別碼 - ${selectedGroup?.group_name}`} open={visible} onCancel={onCancel} width={900} footer={modalFooter}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {selectedSubIdentifiers && selectedSubIdentifiers.length > 0 && (
            <Space>
              <span>目前已選擇:</span>
              <span style={{ fontWeight: "bold" }}>{selectedNames.join(", ") || selectedSubIdentifiers.join(", ")}</span>
            </Space>
          )}
          <Input
            className="search-input"
            placeholder="搜尋名稱"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredOptions}
        rowKey="id"
        loading={loading}
        rowSelection={{
          type: "checkbox", // 改為多選
          selectedRowKeys,
          onChange: handleRowSelectionChange,
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record), // 點擊行時切換選中狀態
          style: { cursor: "pointer" }, // 改變滑鼠指針樣式，提示可點擊
        })}
        pagination={{
          defaultPageSize: 10,
          showTotal: (total) => `共 ${total} 筆資料`,
        }}
      />
    </Modal>
  );
};